/**
 * Account di piattaforma (V56): dove è registrata un'email.
 *
 * Risponde alla domanda che nasce ogni volta che un invito viene rifiutato
 * perché l'indirizzo è già di un'altra società. Ricerca per parte
 * dell'email, in sola lettura: gli account li gestisce l'Admin Squadra della
 * loro società. Il testo cercato sta nella query string, così il messaggio
 * di V54 può aprire la ricerca già fatta.
 */

import { type FormEvent, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MINIMO_RICERCA } from '@calcio/shared';
import { InfoIcon } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { useDati } from '../comuni/dati.js';
import { ETICHETTA_RUOLO, nomeSquadra } from '../amministrazione/ruoli.js';
import { type RicercaAccount, giorno, statoAccount } from './piattaforma.js';

export function AccountPiattaforma() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const cercato = new URLSearchParams(search).get('q')?.trim() ?? '';
  const [testo, setTesto] = useState(cercato);
  const valido = cercato.length >= MINIMO_RICERCA;
  const { dati, errore } = useDati<RicercaAccount>(
    valido ? `/piattaforma/account?q=${encodeURIComponent(cercato)}` : '',
  );

  const cerca = (e: FormEvent) => {
    e.preventDefault();
    const q = testo.trim();
    navigate(q ? `${pathname}?q=${encodeURIComponent(q)}` : pathname, { replace: true });
  };

  return (
    <>
      <Testata titolo="Account" voce="piattaforma-account" />
      <Corpo>
        <div className="flex flex-col gap-4">
          <form onSubmit={cerca} className="max-w-xl">
            <Field>
              <FieldLabel htmlFor="cerca-email">Email</FieldLabel>
              <div className="flex gap-3">
                <Input
                  id="cerca-email"
                  type="search"
                  value={testo}
                  placeholder="nome@società.it, o una sua parte"
                  onChange={(e) => setTesto(e.target.value)}
                />
                <Button type="submit">Cerca</Button>
              </div>
              <FieldDescription>
                Almeno {MINIMO_RICERCA} caratteri. Mostra la società di appartenenza di ogni
                account.
              </FieldDescription>
            </Field>
          </form>

          {!valido ? (
            cercato && (
              <Alert>
                <InfoIcon />
                <AlertTitle>Ricerca troppo corta</AlertTitle>
                <AlertDescription>
                  Scrivi almeno {MINIMO_RICERCA} caratteri dell'email.
                </AlertDescription>
              </Alert>
            )
          ) : !dati ? (
            <StatoCaricamento errore={errore} />
          ) : dati.account.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyTitle>Nessun account</EmptyTitle>
                <EmptyDescription>
                  Nessuna email contiene «{cercato}»: l'indirizzo è libero.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <>
              {dati.altri && (
                <Alert>
                  <InfoIcon />
                  <AlertTitle>Risultati parziali</AlertTitle>
                  <AlertDescription>
                    Mostrati i primi {dati.account.length}: scrivi una parte più lunga dell'email
                    per trovare gli altri.
                  </AlertDescription>
                </Alert>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Account</TableHead>
                    <TableHead>Società</TableHead>
                    <TableHead className="whitespace-normal">Ruoli</TableHead>
                    <TableHead>Ultimo accesso</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dati.account.map((a) => {
                    const stato = statoAccount(a);
                    return (
                      <TableRow key={a.id}>
                        <TableCell>
                          <span className="flex flex-col">
                            <span className="font-medium">{a.email}</span>
                            <span className="text-xs text-muted-foreground">
                              {a.nome} {a.cognome}
                            </span>
                          </span>
                        </TableCell>
                        <TableCell className="whitespace-normal">
                          <span className="flex flex-wrap items-center gap-2">
                            {a.societa && (
                              <Link
                                to={`/piattaforma/societa/${a.societa.id}`}
                                className="hover:underline"
                              >
                                {a.societa.nome}
                              </Link>
                            )}
                            <Badge
                              variant={
                                stato.tono === 'attenzione'
                                  ? 'destructive'
                                  : stato.tono === 'spento'
                                    ? 'secondary'
                                    : 'outline'
                              }
                            >
                              {stato.etichetta}
                            </Badge>
                          </span>
                        </TableCell>
                        <TableCell className="whitespace-normal">
                          <span className="flex flex-wrap gap-1">
                            {(a.societa?.ruoli ?? []).map((r, i) => (
                              <Badge key={i} variant={r.squadra ? 'outline' : 'default'}>
                                {ETICHETTA_RUOLO[r.ruolo]}
                                {r.squadra &&
                                  ` · ${nomeSquadra({ squadra: r.squadra, categoria: r.categoria })} ${r.stagione ?? ''}`}
                              </Badge>
                            ))}
                            {a.societa && a.societa.ruoli.length === 0 && (
                              <span className="text-sm text-muted-foreground">Nessun ruolo</span>
                            )}
                          </span>
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {a.ultimoAccesso ? giorno(a.ultimoAccesso) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}
        </div>
      </Corpo>
    </>
  );
}
